import chroma from 'chroma-js';

/**
 * Shared lightning-strike age styling, modelled on maps.blitzortung.org:
 * colour encodes a strike's age within a rolling window — newest white,
 * fading through yellow/orange/red to dark red at the end of the window.
 *
 * This is the single source of truth consumed by both map overlays
 * (webapp maplibre layer + mobile markers) and both legends, so the map
 * and its legend can never drift apart.
 */

/** Rolling age window for lightning strikes (Blitzortung shows ~20 min). */
export const LIGHTNING_AGE_WINDOW_MIN = 20;
export const LIGHTNING_AGE_WINDOW_MS = LIGHTNING_AGE_WINDOW_MIN * 60 * 1000;

/** Age (minutes) → colour stops. Newest first, oldest last. */
export const LIGHTNING_AGE_STOPS: ReadonlyArray<readonly [number, string]> = [
  [0, '#FFFFFF'], // white — just struck
  [4, '#FFE34D'], // pale yellow
  [8, '#FFA500'], // orange
  [13, '#FF2A00'], // red
  [20, '#8B0000'], // dark red — about to be pruned
];

/** Dark outline so near-white fresh strikes stay visible on a light basemap. */
export const LIGHTNING_STROKE_COLOR = '#37474F';

const clampAge = (ageMin: number) =>
  Math.max(0, Math.min(LIGHTNING_AGE_WINDOW_MIN, ageMin));

const ageScale = chroma
  .scale(LIGHTNING_AGE_STOPS.map(([, color]) => color))
  .domain(LIGHTNING_AGE_STOPS.map(([min]) => min));

/** Colour for a strike of the given age in minutes (clamped to the window). */
export const lightningAgeColor = (ageMin: number): string =>
  ageScale(clampAge(ageMin)).hex();

/** Opacity for a strike of the given age (fresh fully opaque, oldest faded). */
export const lightningAgeOpacity = (ageMin: number): number => {
  const t = clampAge(ageMin) / LIGHTNING_AGE_WINDOW_MIN;
  return Number((1 - 0.6 * t).toFixed(2)); // 1.0 → 0.4
};

/** Marker radius (px) for a strike of the given age (fresh largest). */
export const lightningAgeRadius = (ageMin: number): number => {
  const t = clampAge(ageMin) / LIGHTNING_AGE_WINDOW_MIN;
  return Number((6 - 3 * t).toFixed(2)); // 6 → 3
};

/**
 * maplibre `interpolate` expression mapping the per-feature `ageMin`
 * property to the same continuous colour ramp as {@link lightningAgeColor}.
 */
export const lightningColorInterpolate = (ageProp = 'ageMin') => [
  'interpolate',
  ['linear'],
  ['get', ageProp],
  ...LIGHTNING_AGE_STOPS.flatMap(([min, color]) => [min, color]),
];

/** Evenly spaced legend swatches (minutes + colour) across the age window. */
export const lightningLegendSwatches = (
  count = 6,
): Array<{ageMin: number; color: string}> =>
  Array.from({length: count}, (_, i) => {
    const ageMin = (LIGHTNING_AGE_WINDOW_MIN * i) / (count - 1);
    return {ageMin, color: lightningAgeColor(ageMin)};
  });

/** CSS `linear-gradient(...)` stops string for the webapp legend bar. */
export const lightningGradientCss = (): string => {
  const stops = LIGHTNING_AGE_STOPS.map(
    ([min, color]) =>
      `${color} ${Math.round((min / LIGHTNING_AGE_WINDOW_MIN) * 100)}%`,
  );
  return `linear-gradient(to right, ${stops.join(', ')})`;
};
